import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import { BrandMark } from '../components/BrandMark';
import { useAuth } from '../context/AuthContext';
import { peekPreferFactoryLogin, peekWechatFactoryReturn } from '../lib/factoryContext';
import { authService } from '../services/authService';
import { markOnboardingDone } from '../utils/onboardingSession';

function parseOAuthHash(): {
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
} {
  const raw = window.location.hash.replace(/^#/, '');
  if (!raw) return {};
  const params = new URLSearchParams(raw);
  const accessToken = params.get('access_token') || undefined;
  const refreshToken = params.get('refresh_token') || undefined;
  const expiresInRaw = params.get('expires_in');
  const expiresIn = expiresInRaw ? Number(expiresInRaw) : undefined;
  return { accessToken, refreshToken, expiresIn: Number.isFinite(expiresIn) ? expiresIn : undefined };
}

function parseOAuthQuery(): { error?: string; message?: string } {
  const params = new URLSearchParams(window.location.search);
  return {
    error: params.get('oauth_error') || undefined,
    message: params.get('oauth_message') || undefined,
  };
}

export function WechatOAuthCallbackPage() {
  const navigate = useNavigate();
  const { status } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(true);

  useEffect(() => {
    void (async () => {
      const queryError = parseOAuthQuery();
      if (queryError.error) {
        setError(queryError.message || '微信登录失败');
        setWorking(false);
        return;
      }

      const tokens = parseOAuthHash();
      if (!tokens.accessToken) {
        setError('未收到登录凭证，请重试');
        setWorking(false);
        return;
      }

      try {
        await authService.completeOAuthLogin({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          expiresIn: tokens.expiresIn,
        });
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
        const intentFactory =
          new URLSearchParams(window.location.search).get('intent') === 'factory'
          || peekWechatFactoryReturn()
          || peekPreferFactoryLogin();
        if (intentFactory) markOnboardingDone();
        navigate(new URLSearchParams(window.location.search).get('intent') === 'learning-share' ? '/join/continue' : intentFactory ? '/login?intent=factory' : '/', { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : '微信登录失败');
        setWorking(false);
      }
    })();
  }, [navigate]);

  if (status === 'signed-in' && !working && !error) {
    const intentFactory =
      new URLSearchParams(window.location.search).get('intent') === 'factory'
      || peekWechatFactoryReturn()
      || peekPreferFactoryLogin();
    return <Navigate to={new URLSearchParams(window.location.search).get('intent') === 'learning-share' ? '/join/continue' : intentFactory ? '/login?intent=factory' : '/'} replace />;
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#F7F4EF] px-4">
      <div className="w-full max-w-md rounded-2xl border border-[#E8E2D8] bg-white p-8 shadow-sm">
        <div className="mb-6 flex justify-center">
          <BrandMark />
        </div>
        <h1 className="text-center text-lg font-semibold text-[#1F1A17]">微信登录</h1>
        {working ? (
          <p className="mt-4 text-center text-sm text-muted">正在打开微信扫码…</p>
        ) : (
          <>
            <p
              className={
                /即将开通|小程序扫码/.test(error || '')
                  ? 'mt-4 rounded-xl border border-[#E8E2D8] bg-[#F7F4EF] px-4 py-3 text-sm text-[#1F1A17]'
                  : 'mt-4 rounded-xl border border-[#F2C6CC] bg-[#FFF3F3] px-4 py-3 text-sm text-danger'
              }
            >
              {error || '微信登录失败'}
            </p>
            <div className="mt-6 flex justify-center">
              <Link to="/login" className="text-sm font-medium text-primary hover:underline">
                返回登录页
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
